import { Card, CardContent } from '@crowdfpl/ui-web/card';
import { Swords } from 'lucide-react';
import { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { EmailVerificationBanner } from '@/components/auth/EmailVerificationBanner';
import { DuelCard } from '@/components/duel-card';
import { CompactDuelSuggestion } from '@/components/duels/CompactDuelSuggestion';
import { DuelsOnboarding } from '@/components/duels/DuelsOnboarding';
import { DuelsSearchBarDesktop } from '@/components/duels/DuelsSearchBarDesktop';
import { DuelsSearchBarMobile } from '@/components/duels/DuelsSearchBarMobile';
import { FplIdPromptModal } from '@/components/duels/FplIdPromptModal';
import { GameweekHeader } from '@/components/duels/GameweekHeader';
import { MobileFiltersBar } from '@/components/duels/MobileFiltersBar';
import { ViewModeSelector } from '@/components/duels/ViewModeSelector';
import SEO from '@/components/SEO';
import { Footer } from '@/components/shared/layout/footer';
import { GlobalHeader } from '@/components/shared/layout/global-header';
import { MobileBottomNavigation } from '@/components/shared/layout/mobile-bottom-navigation';
import { MobileHeader } from '@/components/shared/layout/mobile-header';
import { POSITION_TO_NUMBER, STUB_TEAM } from '@/constants';
import { useAuth } from '@/hooks/auth';
import { useDuels } from '@/hooks/data/duels';
import { useVoteOnDuel } from '@/hooks/data/duelVotes';
import { useFeaturedCollections } from '@/hooks/data/featured-collections';
import { useGameweekStatuses, GameweekStatus as GWStatus } from '@/hooks/data/gameweekStatuses';
import { usePlayersPriceRange } from '@/hooks/data/players';
import { useMyProfile } from '@/hooks/data/profiles';
import { useTeams } from '@/hooks/data/teams';
import { useDuelsPlayerHydration } from '@/hooks/useDuelsPlayerHydration';
import { useIsMobile, useIsMobileNav } from '@/hooks/utils/use-mobile';
import { useProductViewTrack } from '@/hooks/utils/use-product-view-track';
import { useStableDuelOrder } from '@/hooks/utils/use-stable-duel-order';
import { notify } from '@/services/notifications';
import { useAuthModal } from '@/stores/ui';
import { useVotesStore } from '@/stores/votes';
import type { ViewMode } from '@/types/featured-collections';
import type { PlayerDto } from '@/types/players';

// ──────────────────────────────────────────────
// URL ↔ ViewMode serialization helpers
// ──────────────────────────────────────────────
function serializeViewMode(vm: ViewMode): string | null {
  switch (vm.type) {
    case 'all': return null;
    case 'featured': return `featured:${vm.collectionSlug}`;
    default: return vm.type;
  }
}

function deserializeViewMode(raw: string | null): ViewMode {
  if (!raw) return { type: 'all' };
  if (raw.startsWith('featured:')) {
    const slug = raw.slice('featured:'.length);
    return slug ? { type: 'featured', collectionSlug: slug } : { type: 'all' };
  }
  if (raw === 'my-voted' || raw === 'my-unvoted' || raw === 'my-duels') return { type: raw };
  return { type: 'all' };
}

const Duels = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showAuthModal } = useAuthModal();
  const { isAuthenticated } = useAuth();
  const isMobile = useIsMobile();
  const isMobileNav = useIsMobileNav();

  useProductViewTrack('duels');

  useEffect(() => {
    if (location.state?.passwordResetSuccess) {
      notify.success('Password updated! Please sign in with your new password.');
      showAuthModal();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, showAuthModal, navigate]);

  // ── Gameweek ───────────────────────────────
  const { groupedGameweeks, selectedGameweek, setSelectedGameweek, currentGameweek, isOffSeason, loading: gameweekLoading } = useGameweekStatuses();

  const isLiveGameweek = useMemo(() => {
    if (!selectedGameweek?.deadline_time) return false;
    return selectedGameweek.is_current && Date.now() >= new Date(selectedGameweek.deadline_time).getTime();
  }, [selectedGameweek]);

  // ── Filter state ───────────────────────────
  const [viewMode, setViewMode] = useState<ViewMode>({ type: 'all' });
  const [selectedPosition, setSelectedPosition] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [selectedPlayerA, setSelectedPlayerA] = useState<PlayerDto | null>(null);
  const [selectedPlayerB, setSelectedPlayerB] = useState<PlayerDto | null>(null);
  const [newDuelsActive, setNewDuelsActive] = useState(false);
  const [myFplTeamActive, setMyFplTeamActive] = useState(false);
  const [showFplIdModal, setShowFplIdModal] = useState(false);
  const [priceRange, setPriceRange] = useState<[number, number]>([40, 150]);

  // ── Price range from API ───────────────────
  const { data: myProfile } = useMyProfile();
  const { data: priceRangeData } = usePlayersPriceRange();
  const priceMinMax: [number, number] = useMemo(
    () => (priceRangeData ? [priceRangeData.min, priceRangeData.max] : [40, 150]),
    [priceRangeData],
  );

  useEffect(() => {
    if (!priceRangeData) return;
    setPriceRange((prev) => {
      const isDefault = prev[0] === 40 && prev[1] === 150;
      return isDefault ? [priceRangeData.min, priceRangeData.max] : prev;
    });
  }, [priceRangeData]);

  // ── Inline URL sync: read on mount ─────────
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (isInitialized || gameweekLoading || !groupedGameweeks) return;

    const gwId = searchParams.get('gameweek');
    if (gwId) {
      const allGws = [
        ...(groupedGameweeks['current'] || []),
        ...(groupedGameweeks['upcoming'] || []),
        ...(groupedGameweeks['closed'] || []),
      ];
      const found = allGws.find((gw) => gw.id === gwId);
      if (found && found.id !== selectedGameweek?.id) setSelectedGameweek(found);
    }

    const pos = searchParams.get('position');
    if (pos) setSelectedPosition(pos);

    const team = searchParams.get('team');
    if (team) setSelectedTeam(team);

    const vm = searchParams.get('viewMode');
    if (vm) setViewMode(deserializeViewMode(vm));

    if (searchParams.get('newDuels') === '1') setNewDuelsActive(true);
    if (searchParams.get('myFplTeam') === '1') setMyFplTeamActive(true);

    const pMin = searchParams.get('priceMin');
    const pMax = searchParams.get('priceMax');
    if (pMin !== null && pMax !== null) {
      const mn = parseInt(pMin, 10);
      const mx = parseInt(pMax, 10);
      if (!Number.isNaN(mn) && !Number.isNaN(mx)) setPriceRange([mn, mx]);
    }

    const paId = searchParams.get('playerA');
    const paName = searchParams.get('playerAName');
    if (paId) {
      setSelectedPlayerA({ id: paId, fpl_id: 0, web_name: paName ?? '', element_type: 0, team: STUB_TEAM } as PlayerDto);
    }
    const pbId = searchParams.get('playerB');
    const pbName = searchParams.get('playerBName');
    if (pbId) {
      setSelectedPlayerB({ id: pbId, fpl_id: 0, web_name: pbName ?? '', element_type: 0, team: STUB_TEAM } as PlayerDto);
    }

    setIsInitialized(true);
  }, [searchParams, groupedGameweeks, selectedGameweek?.id, setSelectedGameweek, gameweekLoading, isInitialized]);

  // ── Inline URL sync: write on state change ─
  useEffect(() => {
    if (!isInitialized) return;

    const id = setTimeout(() => {
      const p = new URLSearchParams();
      if (selectedGameweek?.id) p.set('gameweek', selectedGameweek.id);
      const vm = serializeViewMode(viewMode);
      if (vm) p.set('viewMode', vm);
      if (selectedPosition) p.set('position', selectedPosition);
      if (selectedTeam) p.set('team', selectedTeam);
      if (selectedPlayerA?.id) {
        p.set('playerA', selectedPlayerA.id);
        if (selectedPlayerA.web_name) p.set('playerAName', selectedPlayerA.web_name);
      }
      if (selectedPlayerB?.id) {
        p.set('playerB', selectedPlayerB.id);
        if (selectedPlayerB.web_name) p.set('playerBName', selectedPlayerB.web_name);
      }
      if (newDuelsActive) p.set('newDuels', '1');
      if (myFplTeamActive) p.set('myFplTeam', '1');
      if (priceRange[0] !== priceMinMax[0] || priceRange[1] !== priceMinMax[1]) {
        p.set('priceMin', String(priceRange[0]));
        p.set('priceMax', String(priceRange[1]));
      }

      if (searchParams.toString() !== p.toString()) {
        setSearchParams(p, { replace: true });
      }
    }, 100);

    return () => clearTimeout(id);
  }, [
    isInitialized, selectedGameweek?.id, viewMode, selectedPosition, selectedTeam,
    selectedPlayerA?.id, selectedPlayerA?.web_name, selectedPlayerB?.id, selectedPlayerB?.web_name,
    newDuelsActive, myFplTeamActive, priceRange, priceMinMax, searchParams, setSearchParams,
  ]);

  // ── URL cleanup on unmount ─────────────────
  useEffect(() => {
    return () => {
      if (!window.location.pathname.startsWith('/duels')) return;
      const cur = new URLSearchParams(window.location.search);
      const gwId = cur.get('gameweek');
      const clean = new URLSearchParams();
      if (gwId) clean.set('gameweek', gwId);
      const hasFilters = cur.has('position') || cur.has('team') || cur.has('viewMode') ||
        cur.has('playerA') || cur.has('playerB') || cur.has('newDuels') ||
        cur.has('myFplTeam') || cur.has('priceMin') || cur.has('priceMax');
      if (hasFilters) {
        const url = clean.toString() ? `${window.location.pathname}?${clean.toString()}` : window.location.pathname;
        window.history.replaceState({}, '', url);
      }
    };
  }, []);

  // ── SEO structured data ────────────────────
  const duelsStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    'name': `Fantasy Premier League Player Duels - ${selectedGameweek?.name || 'Gameweek'}`,
    'description': `Vote on head-to-head FPL player predictions for ${selectedGameweek?.name || 'current gameweek'}. See crowd wisdom and make better Fantasy Premier League decisions.`,
    'breadcrumb': {
      '@type': 'BreadcrumbList',
      'itemListElement': [
        { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://crowdfpl.com/' },
        { '@type': 'ListItem', 'position': 2, 'name': 'Duels', 'item': 'https://crowdfpl.com/duels' },
      ],
    },
  };

  const voteMutation = useVoteOnDuel();
  const { isVotingByDuelId, setVoting } = useVotesStore();

  // ── Derived filter values ──────────────────
  const { data: teamsData } = useTeams({ season_id: selectedGameweek?.season_id, limit: 100 });
  const allTeams = useMemo(
    () => (teamsData?.teams || []).map((t) => t.short_name).sort(),
    [teamsData],
  );

  const playerIds = useMemo(() => {
    const ids = [selectedPlayerA?.id, selectedPlayerB?.id].filter(Boolean) as string[];
    return ids.length > 0 ? ids : undefined;
  }, [selectedPlayerA?.id, selectedPlayerB?.id]);

  const positionNumber = useMemo(
    () => (selectedPosition ? POSITION_TO_NUMBER[selectedPosition] : undefined),
    [selectedPosition],
  );

  const teamId = useMemo(
    () => teamsData?.teams.find((t) => t.short_name === selectedTeam)?.id,
    [teamsData?.teams, selectedTeam],
  );

  const myVoteFilter = useMemo((): 'voted' | 'unvoted' | undefined => {
    if (viewMode.type === 'my-voted') return 'voted';
    if (viewMode.type === 'my-unvoted') return 'unvoted';
    return undefined;
  }, [viewMode]);

  const featuredCollectionSlug = useMemo(
    () => (viewMode.type === 'featured' ? viewMode.collectionSlug : undefined),
    [viewMode],
  );

  const createdAfter = useMemo(() => {
    if (!newDuelsActive || !groupedGameweeks) return undefined;
    const currentGW = groupedGameweeks[GWStatus.CURRENT]?.[0];
    if (!currentGW?.deadline_time) return undefined;
    const deadlineMs = new Date(currentGW.deadline_time).getTime();
    const since = Date.now() - 24 * 60 * 60 * 1000;
    return new Date(Math.max(since, deadlineMs)).toISOString();
  }, [newDuelsActive, groupedGameweeks]);

  const handleMyFplTeamToggle = useCallback(() => {
    if (myFplTeamActive) { setMyFplTeamActive(false); return; }
    if (!isAuthenticated) { showAuthModal(); return; }
    if (!myProfile?.fpl_id) { setShowFplIdModal(true); return; }
    setMyFplTeamActive(true);
  }, [myFplTeamActive, isAuthenticated, myProfile?.fpl_id, showAuthModal]);

  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
  }, []);

  const handleClearAll = useCallback(() => {
    setSelectedPosition('');
    setSelectedTeam('');
    setSelectedPlayerA(null);
    setSelectedPlayerB(null);
    if (newDuelsActive) setNewDuelsActive(false);
    if (myFplTeamActive) setMyFplTeamActive(false);
    setPriceRange(priceMinMax);
  }, [newDuelsActive, myFplTeamActive, priceMinMax]);

  // ── Featured collections ───────────────────
  const { data: featuredCollectionsResponse, isLoading: featuredCollectionsLoading } = useFeaturedCollections(
    selectedGameweek?.id,
  );

  // ── Fetch duels ────────────────────────────
  const duelsQueryEnabled = !(viewMode.type === 'my-duels' && !isAuthenticated);

  const { duels, totalCount, loading, isFetchingNextPage, hasNextPage, fetchNextPage } = useDuels({
    gameweekId: selectedGameweek?.id,
    featuredCollectionSlug,
    playerIds,
    position: positionNumber,
    teamId,
    myVote: myVoteFilter,
    myDuels: viewMode.type === 'my-duels' && isAuthenticated ? true : undefined,
    createdAfter,
    fplTeam: myFplTeamActive || undefined,
    priceMin: priceRange[0] !== priceMinMax[0] || priceRange[1] !== priceMinMax[1] ? priceRange[0] : undefined,
    priceMax: priceRange[0] !== priceMinMax[0] || priceRange[1] !== priceMinMax[1] ? priceRange[1] : undefined,
    enabled: duelsQueryEnabled,
  });

  useDuelsPlayerHydration(duels, selectedPlayerA, selectedPlayerB, setSelectedPlayerA, setSelectedPlayerB);

  // ── Infinite scroll ────────────────────────
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const tryLoadMore = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;

    const maybeLoadByPosition = () => {
      const rect = el.getBoundingClientRect();
      if (rect.top <= window.innerHeight + 200) {
        tryLoadMore();
      }
    };

    let observer: IntersectionObserver | null = null;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            tryLoadMore();
          }
        },
        { threshold: 0, rootMargin: '200px 0px' },
      );
      observer.observe(el);
    }

    // Fallback for mobile browsers where IntersectionObserver can be flaky.
    window.addEventListener('scroll', maybeLoadByPosition, { passive: true });
    window.addEventListener('resize', maybeLoadByPosition);
    maybeLoadByPosition();

    return () => {
      observer?.disconnect();
      window.removeEventListener('scroll', maybeLoadByPosition);
      window.removeEventListener('resize', maybeLoadByPosition);
    };
  }, [tryLoadMore, duels.length]);

  // ── Vote handler ───────────────────────────
  const handleVote = useCallback(async (duelId: string, side: string) => {
    if (!isAuthenticated) { showAuthModal(); return; }
    const duel = duels.find((d) => d.id === duelId);
    if (!duel) return;
    if (selectedGameweek?.status === GWStatus.CLOSED) { notify.info('Voting closed'); return; }
    const selected_player_id = side === 'player1' ? duel.playerAId : duel.playerBId;
    if (!selected_player_id) return;
    setVoting(duelId, true);
    try { await voteMutation.submitVote({ duel_id: duelId, selected_player_id }); }
    finally { setVoting(duelId, false); }
  }, [duels, isAuthenticated, selectedGameweek?.status, setVoting, showAuthModal, voteMutation]);

  // ── Stable list order (freeze on vote; reset on GW / filters) ────
  const duelOrderScopeKey = useMemo(
    () =>
      JSON.stringify({
        gw: selectedGameweek?.id ?? '',
        view: viewMode,
        position: selectedPosition,
        team: selectedTeam,
        playerA: selectedPlayerA?.id ?? null,
        playerB: selectedPlayerB?.id ?? null,
        newDuels: newDuelsActive,
        fplTeam: myFplTeamActive,
        price: priceRange,
      }),
    [
      selectedGameweek?.id,
      viewMode,
      selectedPosition,
      selectedTeam,
      selectedPlayerA?.id,
      selectedPlayerB?.id,
      newDuelsActive,
      myFplTeamActive,
      priceRange,
    ],
  );

  const displayDuels = useStableDuelOrder(duels, duelOrderScopeKey);

  const existingDuelExists = useMemo(() => {
    if (!selectedPlayerA || !selectedPlayerB) return false;
    return duels.some((d) => {
      const ids = new Set([d.playerAId, d.playerBId]);
      return ids.has(selectedPlayerA.id) && ids.has(selectedPlayerB.id);
    });
  }, [duels, selectedPlayerA, selectedPlayerB]);

  // ── Mobile search handler ──────────────────
  const toPlayerStub = (p: { id: string; fpl_id: number; web_name: string } | null): PlayerDto | null => {
    if (!p) return null;
    return { id: p.id, fpl_id: p.fpl_id, web_name: p.web_name, element_type: 0, team: STUB_TEAM } as PlayerDto;
  };

  const handleSearchFiltersChange = (
    _query: string,
    playerA: { id: string; fpl_id: number; web_name: string } | null,
    playerB: { id: string; fpl_id: number; web_name: string } | null,
  ) => {
    setSelectedPlayerA(toPlayerStub(playerA));
    setSelectedPlayerB(toPlayerStub(playerB));
  };

  // ── Instruction text ───────────────────────
  const instructionText = useMemo(() => {
    const isNext = selectedGameweek?.is_next === true;
    const oneSelected = (!!selectedPlayerA !== !!selectedPlayerB);
    if (isNext) {
      if (oneSelected) return 'Select a second player to create or find duel';
      return 'Select players to create or find duels';
    }
    return 'Select players to find duels';
  }, [selectedGameweek?.is_next, selectedPlayerA, selectedPlayerB]);

  // ── Render ─────────────────────────────────
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SEO
        title={`FPL Player Duels - ${selectedGameweek?.name || 'Gameweek Predictions'}`}
        description={`Vote on head-to-head Fantasy Premier League player predictions for ${selectedGameweek?.name || 'current gameweek'}. See crowd wisdom, compare players, and make better FPL decisions.`}
        keywords="FPL duels, Fantasy Premier League predictions, player comparison, gameweek predictions, FPL voting, crowd wisdom"
        structuredData={duelsStructuredData}
      />

      {isMobileNav && <MobileHeader onAuthModalOpen={showAuthModal} />}
      {!isMobileNav && <GlobalHeader onAuthModalOpen={showAuthModal} />}

      <div className="container mx-auto px-4">
        <EmailVerificationBanner className={isMobile ? 'mt-2' : 'mt-4'} />
      </div>

      <div className={`container mx-auto flex-1 px-4 py-3 space-y-4 ${isMobileNav ? 'pb-20' : ''}`}>
        <div className="space-y-3">
          <GameweekHeader
            groupedGameweeks={groupedGameweeks}
            selectedGameweek={selectedGameweek}
            setSelectedGameweek={setSelectedGameweek}
            isOffSeason={isOffSeason}
          />

          <ViewModeSelector
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            isAuthenticated={isAuthenticated}
            onAuthRequired={showAuthModal}
            compact={isMobile}
            collections={featuredCollectionsResponse?.collections ?? []}
            collectionsLoading={featuredCollectionsLoading}
          />

          {/* Desktop: DuelsSearchBarDesktop (Tier 2 + Tier 3) */}
          {!isMobile && (
            <DuelsSearchBarDesktop
              selectedPosition={selectedPosition}
              setSelectedPosition={setSelectedPosition}
              selectedTeam={selectedTeam}
              setSelectedTeam={setSelectedTeam}
              onPlayersChange={(a, b) => {
                setSelectedPlayerA(a);
                setSelectedPlayerB(b);
              }}
              selectedPlayerA={selectedPlayerA}
              selectedPlayerB={selectedPlayerB}
              setSelectedPlayerA={setSelectedPlayerA}
              setSelectedPlayerB={setSelectedPlayerB}
              gameweekId={selectedGameweek?.id}
              isNextWeek={selectedGameweek?.is_next === true}
              existingDuelExists={existingDuelExists}
              newDuelsActive={newDuelsActive}
              onNewDuelsToggle={() => setNewDuelsActive((v) => !v)}
              myFplTeamActive={myFplTeamActive}
              onMyFplTeamToggle={handleMyFplTeamToggle}
              priceRange={priceRange}
              priceMinMax={priceMinMax}
              onPriceRangeChange={setPriceRange}
              onClearAll={handleClearAll}
            />
          )}

          {/* Mobile: Search + MobileFiltersBar */}
          {isMobile && (
            <div className="mobile-search-filters">
              <div className="mobile-search-section">
                <DuelsSearchBarMobile
                  onFiltersChange={handleSearchFiltersChange}
                  selectedPlayerA={selectedPlayerA ? { id: selectedPlayerA.id, fpl_id: selectedPlayerA.fpl_id, web_name: selectedPlayerA.web_name } : null}
                  selectedPlayerB={selectedPlayerB ? { id: selectedPlayerB.id, fpl_id: selectedPlayerB.fpl_id, web_name: selectedPlayerB.web_name } : null}
                  gameweekId={selectedGameweek?.id}
                  isNextWeek={selectedGameweek?.is_next === true}
                  existingDuelExists={existingDuelExists}
                  onCreated={() => notify.success('Duel created')}
                />
              </div>

              <div className="px-3 py-2">
                <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                  <Swords className="h-3.5 w-3.5" />
                  {instructionText}
                </p>
              </div>

              <MobileFiltersBar
                selectedPosition={selectedPosition}
                setSelectedPosition={setSelectedPosition}
                selectedTeam={selectedTeam}
                setSelectedTeam={setSelectedTeam}
                selectedPlayerA={selectedPlayerA}
                selectedPlayerB={selectedPlayerB}
                setSelectedPlayerA={setSelectedPlayerA}
                setSelectedPlayerB={setSelectedPlayerB}
                newDuelsActive={newDuelsActive}
                onNewDuelsToggle={() => setNewDuelsActive((v) => !v)}
                myFplTeamActive={myFplTeamActive}
                onMyFplTeamToggle={handleMyFplTeamToggle}
                priceRange={priceRange}
                priceMinMax={priceMinMax}
                onPriceRangeChange={setPriceRange}
                onClearAll={handleClearAll}
                teams={allTeams}
              />
            </div>
          )}

          <DuelsOnboarding />

          {loading || gameweekLoading ? (
            <Card>
              <CardContent className="p-6">
                <div className="text-center py-8">
                  <h2 className="text-lg font-semibold mb-2">Loading duels...</h2>
                </div>
              </CardContent>
            </Card>
          ) : duels.length === 0 ? (
            <Card>
              <CardContent className="p-6 space-y-4">
                {selectedPlayerA && selectedPlayerB && selectedPlayerA.fpl_id !== selectedPlayerB.fpl_id ? (
                  <CompactDuelSuggestion
                    playerA={selectedPlayerA}
                    playerB={selectedPlayerB}
                    gameweekId={selectedGameweek?.id}
                    isNextWeek={selectedGameweek?.is_next === true}
                    onCreated={() => notify.success('Duel created')}
                  />
                ) : (
                  <div className="text-center py-8 text-muted-foreground">No duels available.</div>
                )}
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  {viewMode.type === 'featured'
                    ? `${displayDuels.length} in this collection`
                    : `Showing ${displayDuels.length} of ${totalCount} duels`}
                </div>
              </div>

              {displayDuels.length === 0 ? (
                <div className="mt-2">
                  {selectedPlayerA && selectedPlayerB && selectedPlayerA.fpl_id !== selectedPlayerB.fpl_id ? (
                    isMobile ? (
                      <div className="flex justify-center py-8">
                        <div className="flex flex-col items-center text-center max-w-[520px] w-full">
                          <p className="text-base font-medium text-foreground">No duels found for this selection</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            {selectedGameweek?.is_next === true ? 'Select different players or create a new duel' : 'Select different players'}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <CompactDuelSuggestion
                        playerA={selectedPlayerA}
                        playerB={selectedPlayerB}
                        gameweekId={selectedGameweek?.id}
                        isNextWeek={selectedGameweek?.is_next === true}
                        onCreated={() => notify.success('Duel created')}
                      />
                    )
                  ) : (
                    <div className="text-center py-6 text-muted-foreground">No duels match your filters.</div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-2">
                  {displayDuels.map((d) => (
                    <DuelCard
                      key={d.id}
                      duel={{ id: d.id, slug: d.slug, player1: d.player1, player2: d.player2, votes: d.votes, potential_rating: d.potential_rating, ratingFactors: d.ratingFactors }}
                      onVote={(id, side) => handleVote(id, side)}
                      isVoting={!!isVotingByDuelId[d.id]}
                      state={d.state}
                      isAuthenticated={isAuthenticated}
                      selectedPlayerId={d.selectedPlayerId}
                      playerAPoints={d.player1.gameweek_stats?.total_points}
                      playerBPoints={d.player2.gameweek_stats?.total_points}
                      isLiveGameweek={isLiveGameweek}
                      solioPredictedA={d.gameweekId === currentGameweek?.id ? d.player1.solioProjectedPoints ?? undefined : undefined}
                      solioPredictedB={d.gameweekId === currentGameweek?.id ? d.player2.solioProjectedPoints ?? undefined : undefined}
                      predictionOutcome={d.votes.prediction_outcome}
                      ratingChange={d.votes.rating_change}
                      isDuelOfTheWeek={d.isDuelOfTheWeek}
                      showDOTWBanner={false}
                    />
                  ))}
                </div>
              )}

              {(hasNextPage || isFetchingNextPage || (duels.length > 0 && duels.length === totalCount)) && (
                <div ref={loadMoreRef} className="py-4">
                  {isFetchingNextPage && (
                    <div className="text-center text-muted-foreground">Loading more duels...</div>
                  )}
                  {!hasNextPage && duels.length > 0 && duels.length === totalCount && (
                    <div className="text-center text-muted-foreground text-sm">All duels loaded</div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Footer />
      {isMobileNav && <MobileBottomNavigation />}

      <FplIdPromptModal
        open={showFplIdModal}
        onOpenChange={setShowFplIdModal}
        onSuccess={() => {
          setMyFplTeamActive(true);
          notify.success('FPL ID saved. My FPL Team filter is now on.');
        }}
      />
    </div>
  );
};

export default Duels;
